import { FC, useMemo } from 'react';
import styled from 'styled-components';
import { Attachments } from 'src/graphql/schema.graphql';
import {
  TranslatedItem,
  useTranslateItem,
} from 'src/hooks/directus/useTranslateItem';
import { File as FileIcon } from 'src/components/icons/File';
import { useMediaQuery } from 'src/hooks/useMediaQuery';
import { Markdown } from 'vendor/mods/markdown/components/Markdown';
import { media } from 'src/styles/designs';
import { useTranslate } from 'react-t7e';
import { H2 } from 'src/components/common/typography/Header';
import { motion } from 'framer-motion';
import { makeTransition } from 'src/utils/motion/transitions';
import { transparentize } from 'polished';
import { NeauviaItem } from 'src/models/directus';
import { getDirectusImageAssetUrl } from 'src/services/directus/common';

const Article = styled.article`
  display: flex;
  flex-direction: column;
  padding-bottom: 16px;
  border-bottom: 1px solid
    ${({ theme }) => transparentize(0.75, theme.text.palette.secondary)};
  &:hover,
  &:focus {
    h3 {
      color: ${({ theme }) => theme.text.palette.accent};
    }
  }
  & + & {
    padding-top: 16px;
  }
`;

const ArticleType = styled.span`
  font-weight: 300;
  font-size: 14px;
  line-height: 21px;
  color: ${({ theme }) => theme.text.palette.secondary};
  margin-bottom: 5px;
  text-transform: uppercase;
  ${media.w.lessThan.desktop} {
    display: none;
  }
`;

const ArticleName = styled.h3`
  font-weight: 500;
  font-size: 16px;
  line-height: 18px;
  color: ${({ theme }) => theme.text.palette.primary};
  transition: 0.2s;
  ${media.w.greaterThanOrEqual.desktop} {
    font-size: 20px;
    line-height: 24px;
  }
`;

const ArticleDescription = styled.p`
  font-weight: 300;
  font-size: 14px;
  line-height: 21px;
  color: ${({ theme }) => theme.text.palette.primary};
  margin-top: 12px;
  ${media.w.greaterThanOrEqual.desktop} {
    font-size: 16px;
    line-height: 24px;
    margin-top: 7px;
  }
`;

const ArticleBox = (props: { article: TranslatedItem<Attachments> }) => {
  const { article } = props;

  const { _ } = useTranslate();

  return (
    <Article as="a" href={article.url} target="_blank" rel="noopener">
      <ArticleType>{_('article')}</ArticleType>
      <ArticleName>{article.name}</ArticleName>
      <ArticleDescription>
        <Markdown>{article.description}</Markdown>
      </ArticleDescription>
    </Article>
  );
};

const FileList = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  grid-gap: 24px;
  ${media.w.greaterThanOrEqual.desktop} {
    grid-template-columns: repeat(2, 1fr);
    padding-top: 10px;
    grid-gap: 40px;
  }
`;

const File = styled.a`
  display: flex;
  align-items: center;
  &:hover,
  &:focus {
    h3 {
      color: ${({ theme }) => theme.text.palette.accent};
    }
  }
`;

const IconWrapper = styled.div`
  color: ${({ theme }) => theme.text.palette.accent};
  margin-right: 15px;
`;

const FileBox = (props: { file: TranslatedItem<Attachments> }) => {
  const { file } = props;
  const isDesktop = useMediaQuery(media.w.greaterThanOrEqual.desktop);

  return (
    <File
      href={getDirectusImageAssetUrl(file.file.id, { download: true })}
      target="_blank"
      rel="noopener"
    >
      <IconWrapper>
        <FileIcon size={isDesktop ? 32 : 23} />
      </IconWrapper>
      <div>
        <ArticleName>{file.name}</ArticleName>
      </div>
    </File>
  );
};

const AttachementList = styled.div`
  max-width: 750px;
  & > ${H2} {
    margin-bottom: 20px;
  }
  & + & {
    margin-top: 20px;
  }
`;

export const NotesTab: FC<{
  item: TranslatedItem<NeauviaItem> | undefined;
}> = ({ item }) => {
  const translateItem = useTranslateItem();
  const { _ } = useTranslate();

  const articles = useMemo(
    () =>
      item && 'attachments' in item
        ? item?.attachments
            ?.map((a) => a.attachments_id)
            .filter((a) => a.type === 'article')
            .map(translateItem) ?? []
        : [],
    [item, translateItem],
  );

  const files = useMemo(
    () =>
      item && 'attachments' in item
        ? item?.attachments
            ?.map((a) => a.attachments_id)
            .filter((a) => a.type === 'file')
            .map(translateItem) ?? []
        : [],
    [item, translateItem],
  );

  return (
    <>
      {articles.length > 0 && (
        <AttachementList>
          <H2
            as={motion.h3}
            variants={makeTransition({
              fade: true,
              from: 'bottomMore',
            })}
          >
            {_('Links')}
          </H2>
          <motion.div
            variants={makeTransition({
              fade: true,
              from: 'bottomMore',
            })}
          >
            {articles.map((article) => (
              <ArticleBox key={article.id} article={article} />
            ))}
          </motion.div>
        </AttachementList>
      )}
      {files.length > 0 && (
        <AttachementList>
          <H2
            as={motion.h3}
            variants={makeTransition({
              fade: true,
              from: 'bottomMore',
            })}
          >
            {_('Files to download')}
          </H2>
          <FileList
            variants={makeTransition({
              fade: true,
              from: 'bottomMore',
            })}
          >
            {files.map((file) => (
              <FileBox key={file.id} file={file} />
            ))}
          </FileList>
        </AttachementList>
      )}
    </>
  );
};
